/* eslint-disable max-len */
import { PAGE_ALIGNMENT_OPTION_TEXTS } from "./questionnaireConstants";
import { PAGE_ALIGNMENT_OPTIONS } from "./questionnaireConstants";
import { PAGE_LAYOUT_OPTION_TEXTS } from "./questionnaireConstants";
import { PAGE_LAYOUT_OPTIONS } from "./questionnaireConstants";
import { PAGE_TYPE_OPTION_NEEDS_LICENSE } from "./questionnaireConstants";
import { PAGE_TYPE_OPTION_TEXTS } from "./questionnaireConstants";
import { PAGE_TYPE_OPTIONS } from "./questionnaireConstants";
/**
 * Properties with "json" value types whose payload can contain IDs
 * of things such as assets or element groups should have ID getter
 * and replacement functions. This allows the backend to find a
 * questionnaire's resource dependencies and to modify them e.g. when
 * creating a cloned copy.
 */
// e.g. "jsonFormat": {"[assetId]": "string"}
export function IdFunctionMapGet(value) {
    return value && typeof (value) === "object" ? Object.keys(value) : [];
}
export function IdFunctionMapReplace(value, idMap) {
    const result = {};
    if (value && typeof (value) === "object") {
        for (const id in value) {
            result[idMap(id) || id] = value[id];
        }
    }
    return result;
}
// e.g. "jsonFormat": {"[pageId]": {"[assetId]": "boolean"}},
export function IdFunctionNestedMapGet(value) {
    const ids = [];
    if (value && typeof (value) === "object") {
        for (const key in value) {
            if (value[key] && typeof (value[key]) === "object") {
                ids.push(...Object.keys(value[key]));
            }
        }
    }
    return ids;
}
export function IdFunctionNestedMapReplace(value, idMap) {
    const result = {};
    for (const key in value) {
        if (value[key] && typeof (value[key]) === "object") {
            result[key] = {};
            for (const id in value[key]) {
                result[key][idMap(id) || id] = value[key][id];
            }
        }
        else {
            result[key] = value[key];
        }
    }
    return result;
}
;
export function IdFunctionAllSeparateListsGet(allIds, idKey) {
    function getIds(value) {
        const ids = [];
        if (value && typeof (value) === "object") {
            if (allIds && Array.isArray(value.all)) {
                ids.push(...value.all);
            }
            if (Array.isArray(value.separate)) {
                for (const item of value.separate) {
                    if (item && typeof (item) === "object" &&
                        typeof (item[idKey]) === "string") {
                        ids.push(idKey);
                    }
                }
            }
        }
        return ids;
    }
    return getIds;
}
export function IdFunctionAllSeparateListsReplace(allIds, idKey) {
    function replaceIds(value, idMap) {
        const result = { all: [], separate: [] };
        if (value && typeof (value) === "object") {
            if (allIds && Array.isArray(value.all)) {
                for (const allId of value.all) {
                    result.all.push(idMap(allId) || allId);
                }
            }
            else {
                result.all = value.all;
            }
            if (Array.isArray(value.separate)) {
                for (const item of value.separate) {
                    const newItem = Object.assign({}, item);
                    newItem[idKey] = idMap(item[idKey]) || item[idKey];
                    result.separate.push(newItem);
                }
            }
        }
        return result;
    }
    return replaceIds;
}
const basicTabGroup = {
    group: "basic",
    title: "Basic settings",
    description: "Primary settings that are needed for this setting.",
};
const extraTabGroup = {
    group: "extra",
    title: "Extra settings",
    description: "Additional settings for this setting.",
};
export const QuestionnairePropertyTabGroups = [
    {
        group: "questionnaireGeneral",
        title: "General",
        description: "General settings that govern functionality.",
    }, {
        group: "questionnaireLanguage",
        title: "Language",
        description: "Settings that govern available translations.",
    }, {
        group: "questionnaireStyle",
        title: "Style",
        description: "Settings that govern style and look.",
    }, {
        group: "questionnaireCard",
        title: "Card",
        description: "Settings that govern questionnaire's public display card.",
    }, {
        group: "questionnaireBackgrounds",
        title: "Backgrounds",
        description: "Settings that govern questionnaire's maps and other interactive backgrounds.",
    }, {
        group: "questionnaire3d",
        title: "3D",
        description: "Settings that govern 3d maps and models.",
    }, {
        group: "questionnaireReport",
        title: "Report settings",
        description: "Settings that govern automated PDF reporting.",
    }, {
        group: "questionnaireReportEmail",
        title: "Report email",
        description: "Settings that govern emailing of the PDF report.",
    }, {
        group: "mapGroup",
        title: "Map Backgrounds",
        description: "Group of settings for only map backgrounds",
    },
];
export const QuestionnairePropertyParallelGroups = [
    {
        group: "reportEmailGroup",
        title: "Additional email recipients",
        description: "Settings that govern the email delivery of the report to stakeholders.",
        tabGroups: [basicTabGroup, extraTabGroup],
        tabGroup: "questionnaireReportEmail",
    },
];
/**
 * A map associating questionnaire properties with their unique key strings.
 */
export const QuestionnairePropertyMap = {};
/**
 * A list of recognized questionnaire properties.
 */
export const QuestionnairePropertyList = [
    // //////////////////////// TECHNICAL SETTINGS ///////////////////////////////////////
    //
    // Settings that govern internal functionality
    //
    // ///////////////////////////////////////////////////////////////////////////////////
    {
        "key": "pageNumberOrder",
        "type": "list.number",
        "tabGroup": "",
        "inputType": "custom",
        "title": "Page ordering",
        "description": "Internal property which indicates the order that pages should appear in.",
        "default": null,
        "multilingual": false,
        "hiddenFromInterface": true,
    },
    // //////////////////////// FORMERLY CARD NOW TITLE SETTINGS /////////////////////////////////////////
    //
    // Settings that govern the names and card apprearance of the questionnaire
    //
    // ///////////////////////////////////////////////////////////////////////////////////
    {
        "key": "title",
        "type": "string",
        "tabGroup": "",
        "propertyType": "globalType",
        "inputType": "text",
        "default": null,
        "title": "Questionnaire title",
        "description": "The questionnaire title that will be shown to the respondent.",
        "multilingual": true,
    }, {
        "key": "description",
        "type": "string",
        "tabGroup": "",
        "propertyType": "globalType",
        "inputType": "textarea",
        "default": null,
        "title": "Questionnaire description",
        "description": "A short description that will be show to the respondent.",
        "multilingual": true,
    }, {
        "key": "featureImageAsset",
        "type": "resourceId",
        "isResourceId": "asset",
        "propertyType": "globalType",
        "inputType": "imageAssetId",
        "tabGroup": "questionnaireImage",
        "default": null,
        "title": "Questionnaire feature image",
        "description": "An image asset to use when linking or sharing the questionnaire.",
        "hiddenFromInterface": false,
        "multilingual": false,
    }, {
        "key": "featureImage",
        "type": "url",
        "tabGroup": "questionnaireImage",
        "propertyType": "globalType",
        "inputType": "imageUrl",
        "default": null,
        "title": "Questionnaire card image",
        "description": "An image to use as the questionnaire card background.",
        "hiddenFromInterface": true,
        "multilingual": false,
    }, {
        "key": "showFullImage",
        "type": "boolean",
        "tabGroup": "questionnaireImage",
        "propertyType": "globalType",
        "inputType": "switch",
        "default": false,
        "title": "Show full image",
        "description": "Select this if you want the whole image to be visible in the card. If this is not selected, the image will be scaled to fill out the card.",
        "multilingual": false,
    }, {
        "key": "cardColor",
        "type": "string",
        "tabGroup": "questionnaireInternal",
        "propertyType": "globalType",
        "inputType": "color",
        "default": null,
        "title": "Questionnaire card color",
        "description": "Accent color to use for the questionnaire card.",
        "multilingual": false,
    }, {
        "key": "questionnaireStyleId",
        "type": "string",
        "tabGroup": "",
        "propertyType": "globalType",
        "inputType": "styleId",
        "default": null,
        "title": "Questionnaire style template",
        "description": "The style template to use for the questionnaire.",
        "multilingual": true,
    }, {
        "key": "lightTextColor",
        "type": "boolean",
        "tabGroup": "questionnaireStyle",
        "propertyType": "globalType",
        "inputType": "switch",
        "default": null,
        "title": "Light text color",
        "description": "Use a light text color for the questionnaire card text.",
        "hiddenFromInterface": true,
        "multilingual": false,
    }, {
        // DEPRECIATED
        // DEPRECIATED
        "key": "explicitConsent",
        "type": "boolean",
        "tabGroup": "questionnaireCard",
        "propertyType": "globalType",
        "inputType": "switch",
        "default": null,
        "title": "Ask for explicit publication consent",
        "description": "If set, respondents will be asked to give their explicit consent for publishing responses at the end of the questionnaire. If this setting is not set, implicit consent is assumed.",
        "multilingual": false,
        "hiddenFromInterface": true,
        "isDeprecated": true,
        // DEPRECIATED
        // DEPRECIATED
    },
    // //////////////////////// LANGUAGE SETTINGS /////////////////////////////////////////
    //
    // Settings that govern the availability of translations
    //
    // ///////////////////////////////////////////////////////////////////////////////////
    {
        "key": "availableLanguages",
        "type": "list.string",
        "tabGroup": "questionnaireLanguage",
        "propertyType": "globalType",
        "inputType": "languageOptionMany",
        "default": [],
        "title": "Available languages",
        "description": "The list of languages for which this questionnaire has translations available.",
        "multilingual": false,
    },
    // //////////////////////// STYLE SETTINGS ///////////////////////////////////////////
    //
    // Settings that govern the style and look of the questionnaire
    //
    // ///////////////////////////////////////////////////////////////////////////////////
    {
        "key": "questionnaireLayout",
        "type": "string",
        "tabGroup": "questionnaireStyle",
        "propertyType": "globalType",
        "inputType": "ownOptionList",
        "valueOptions": PAGE_LAYOUT_OPTIONS,
        "valueOptionTexts": PAGE_LAYOUT_OPTION_TEXTS,
        "default": "narrow",
        "title": "Questionnaire layout",
        "description": "What type of layout to use for this questionnaire",
        "multilingual": false,
        "hiddenFromInterface": true,
    }, {
        "key": "questionnaireAlignment",
        "type": "string",
        "tabGroup": "questionnaireStyle",
        "propertyType": "globalType",
        "inputType": "ownOptionList",
        "valueOptions": PAGE_ALIGNMENT_OPTIONS,
        "valueOptionTexts": PAGE_ALIGNMENT_OPTION_TEXTS,
        "default": "center",
        "title": "Questionnaire alignment",
        "description": "What type of alignment to use for this questionnaire",
        "hiddenFromInterface": true,
        "multilingual": false,
    }, {
        "key": "backgroundImageSettings",
        "type": "json",
        "jsonFormat": { "all": "list.resourceId", "separate": [{ "pageId": "string", "mapId": "resourceId" }] },
        "tabGroup": "autoFill",
        "propertyType": "globalType",
        "inputType": "autoFill",
        "default": null,
        "title": "Background image settings",
        "description": "The background image settings.",
        "multilingual": false,
        "getAssetIds": IdFunctionAllSeparateListsGet(true, "mapId"),
        "replaceAssetIds": IdFunctionAllSeparateListsReplace(true, "mapId"),
        "getQuestionnaireElementGroupIds": IdFunctionAllSeparateListsGet(false, "pageId"),
        "replaceQuestionnaireElementGroupIds": IdFunctionAllSeparateListsReplace(false, "pageId"),
    }, {
        "key": "showPageTitles",
        "type": "boolean",
        "tabGroup": "questionnaireStyle",
        "propertyType": "globalType",
        "inputType": "switch",
        "default": null,
        "title": "Show page titles",
        "description": "Show page title on each questionnaire page.",
        "multilingual": false,
    }, {
        "key": "showPageNumbers",
        "type": "boolean",
        "tabGroup": "questionnaireStyle",
        "propertyType": "globalType",
        "inputType": "switch",
        "default": null,
        "title": "Show page numbering",
        "description": "Show a running page number on each questionnaire page.",
        "multilingual": false,
    }, {
        "key": "showProgressBar",
        "type": "boolean",
        "tabGroup": "questionnaireStyle",
        "propertyType": "globalType",
        "inputType": "switch",
        "default": null,
        "title": "Show progress bar",
        "description": "Show a progress bar as the respondent progresses through the questionnaire.",
        "multilingual": false,
    }, {
        "key": "showThankYouMessage",
        "type": "boolean",
        "tabGroup": "questionnaireCard",
        "propertyType": "globalType",
        "inputType": "switch",
        "default": true,
        "title": "Show \"Thank You\" message",
        "description": "Show a \"Thank You\" message after the respondent finished the questionnaire.",
        "multilingual": false,
    }, {
        "key": "thankYouTitle",
        "type": "string",
        "tabGroup": "questionnaireCard",
        "propertyType": "globalType",
        "inputType": "text",
        "default": null,
        "title": "Thank you title",
        "description": "The title for the dialog at the end of the questionnaire.",
        "multilingual": true,
        "showIfProperty": {
            "key": "showThankYouMessage",
            "value": [true],
        },
    }, {
        "key": "thankYouMessage",
        "type": "string",
        "tabGroup": "questionnaireCard",
        "propertyType": "globalType",
        "inputType": "textarea",
        "default": null,
        "title": "Thank you message",
        "description": "The message for the dialog at the end of the questionnaire.",
        "multilingual": true,
        "showIfProperty": {
            "key": "showThankYouMessage",
            "value": [true],
        },
    }, /* {
       "key": "generalLayout",
       "type": "string",
       "tabGroup": "questionnaireStyle",
       "propertyType": "globalType",
       "inputType": "selectLayout",
       "default": null,
       "title": "Choose Layout",
       "description": "Choose the general layout of the questionnaire.",
       "multilingual": false,
    }, */
    // //////////////////////// GENERAL SETTINGS ////////////////////////////////////////////
    //
    // Settings that govern the general type and functionality of the questionnaire
    //
    // ///////////////////////////////////////////////////////////////////////////////////
    {
        "key": "redirectUrl",
        "type": "url",
        "tabGroup": "questionnaireGeneral",
        "propertyType": "globalType",
        "inputType": "url",
        "default": null,
        "title": "Redirect URL at the end",
        "description": "A URL to send respondents to after they finish the questionnaire.",
        "multilingual": true,
    }, {
        "key": "maintainSession",
        "type": "boolean",
        "tabGroup": "questionnaireGeneral",
        "propertyType": "globalType",
        "inputType": "switch",
        "default": true,
        "title": "Maintain respondent session",
        "description": "If set, allows respondents to continue responding after page reload / browser reboot.",
        "multilingual": false,
    }, {
        "key": "suomiFiAuthenticationRequired",
        "type": "boolean",
        "tabGroup": "questionnaireGeneral",
        "propertyType": "globalType",
        "inputType": "switch",
        "default": false,
        "title": "Require suomi.fi authentication",
        // "hiddenFromInterface": true,
        "description": "Require suomi.fi authentication",
        "multilingual": false,
    }, {
        "key": "suomiFiHomeTownNumberList",
        "type": "string",
        "tabGroup": "questionnaireGeneral",
        "propertyType": "globalType",
        "inputType": "text",
        "default": '',
        "title": "Only allow people living in these towns to respond",
        // "hiddenFromInterface": true,
        "description": "Only allow people living in these towns to respond",
        "multilingual": false,
    }, {
        "key": "showPublicResponsesOnLastPage",
        "type": "boolean",
        "tabGroup": "questionnaireGeneral",
        "propertyType": "globalType",
        "inputType": "switch",
        "default": null,
        "title": "Show public responses by others on last page",
        "description": "If you set the 'Show responses by others' setting on any element, those responses will be shown on the last page of the questionnaire",
        "multilingual": false,
        "needsLicense": "feature:questionnaire:response-reactions"
    }, {
        "key": "allowResponseReactions",
        "type": "boolean",
        "tabGroup": "questionnaireGeneral",
        "propertyType": "globalType",
        "inputType": "switch",
        "default": false,
        "title": "Allow respondents to vote for responses of others",
        "description": "This will allow respondents to upvote or downvote responses of others on the map.",
        "multilingual": false,
        "needsLicense": "feature:questionnaire:response-reactions"
    }, {
        "key": "explicitPublicationConsent",
        "type": "boolean",
        "tabGroup": "questionnaireGeneral",
        "propertyType": "globalType",
        "inputType": "switch",
        "default": null,
        "title": "Ask for explicit publication consent",
        "description": "If set, respondents will be asked to give their explicit consent for publishing responses at the end of the questionnaire. If this setting is not set, implicit consent is assumed.",
        "multilingual": false,
    }, {
        "key": "explicitParticipatoryConsent",
        "type": "boolean",
        "tabGroup": "questionnaireGeneral",
        "propertyType": "globalType",
        "inputType": "switch",
        "default": null,
        "title": "Ask for explicit participatory consent",
        "description": "If set, respondents will be asked to give their explicit consent for including responses in participatory projects. If this setting is not set, implicit consent is assumed.",
        "multilingual": false,
        "hiddenFromInterface": true,
    }, {
        "key": "askConsentAtStart",
        "type": "boolean",
        "tabGroup": "questionnaireGeneral",
        "propertyType": "globalType",
        "inputType": "switch",
        "default": null,
        "title": "Consent at start",
        "description": "If set, publication consent will be asked at the start of the questionnaire if 'Ask for explicit publication consent' is set.",
        "multilingual": false,
        "hiddenFromInterface": true,
    }, {
        "key": "privacyPolicy",
        "type": "url",
        "tabGroup": "questionnaireGeneral",
        "propertyType": "globalType",
        "inputType": "url",
        "default": null,
        "title": "Privacy policy URL",
        "description": "A URL to privacy policy for this questionnaire.",
        "multilingual": true,
    }, {
        "key": "allowedEmbedHost",
        "type": "string",
        "tabGroup": "questionnaireGeneral",
        "propertyType": "globalType",
        "inputType": "text",
        "default": null,
        "title": "Allow embedding on",
        "description": "The website domain URL where this questionnaire should be allowed to be embedded.",
        "multilingual": false,
        "needsLicense": "feature:questionnaire:embedding",
    },
    // //////////////////////// MAPS SETTINGS ////////////////////////////////////////////
    //
    // Settings that govern questionnaire's maps and other interactive backgrounds
    //
    // ///////////////////////////////////////////////////////////////////////////////////
    {
        "key": "questionnaireType",
        "type": "string",
        "tabGroup": "questionnaireBackgrounds",
        "propertyType": "globalType",
        "inputType": "ownOptionList",
        "valueOptions": PAGE_TYPE_OPTIONS,
        "valueOptionTexts": PAGE_TYPE_OPTION_TEXTS,
        "valueOptionNeedsLicense": PAGE_TYPE_OPTION_NEEDS_LICENSE,
        "default": "ol",
        "title": "Questionnaire type",
        "description": "What type of background to use for this questionnaire.",
        "multilingual": false,
    }, {
        // DEPRECIATED
        // DEPRECIATED
        "key": "mapOriginZoom",
        "type": "number",
        "tabGroup": "questionnaireBackgrounds",
        "propertyType": "globalType",
        "inputType": "integer",
        "default": null,
        "title": "Initial map zoom level",
        "description": "Show the map at this zoom level when the questionnaire begins.",
        "multilingual": false,
        "hiddenFromInterface": true,
        "isDeprecated": true,
        // DEPRECIATED
        // DEPRECIATED
    }, {
        // DEPRECIATED
        // DEPRECIATED
        "key": "mapOriginLat",
        "type": "number",
        "tabGroup": "questionnaireBackgrounds",
        "propertyType": "globalType",
        "inputType": "number",
        "default": null,
        "title": "Initial map latitude",
        "description": "Show this location on the map when the questionnaire begins.",
        "multilingual": false,
        "hiddenFromInterface": true,
        "isDeprecated": true,
        // DEPRECIATED
        // DEPRECIATED
    }, {
        // DEPRECIATED
        // DEPRECIATED
        "key": "mapOriginLon",
        "type": "number",
        "tabGroup": "questionnaireBackgrounds",
        "propertyType": "globalType",
        "inputType": "number",
        "default": null,
        "title": "Initial map longitude",
        "description": "Show this location on the map when the questionnaire begins.",
        "multilingual": false,
        "hiddenFromInterface": true,
        "isDeprecated": true,
        // DEPRECIATED
        // DEPRECIATED
    }, {
        "key": "scaleUnit",
        "type": "string",
        "inputType": "ownOptionList",
        "valueOptions": ["metric", "imperial"],
        "valueOptionTexts": ["Metric", "Imperial"],
        "tabGroup": "questionnaireBackgrounds",
        "propertyType": "globalType",
        "default": "metric",
        "title": "Interactive map scale unit",
        "description": "Choose which unit to show in the scale line if you are using the interactive map.",
        "multilingual": false,
    }, {
        "key": "backgroundColor",
        "type": "string",
        "tabGroup": "questionnaireBackgrounds",
        "propertyType": "globalType",
        "inputType": "color",
        "default": null,
        "title": "Background color",
        "description": "The background color to use for this questionnaire.",
        "multilingual": false,
    }, {
        "key": "backgroundImages",
        "type": "list.resourceId",
        "isResourceIdList": "asset",
        "tabGroup": "questionnaireBackgrounds",
        "propertyType": "globalType",
        "inputType": "pageLayerSetting",
        "assetGroup": "images",
        "settingsKey": "backgroundImageSettings",
        "default": [],
        "title": "Background images",
        "description": "Background Images to use for this questionnaire.",
        "multilingual": false,
    }, {
        "key": "images",
        "type": "list.resourceId",
        "isResourceIdList": "asset",
        "tabGroup": "questionnaireBackgrounds",
        "propertyType": "globalType",
        "inputType": "pageLayerSetting",
        "assetGroup": "images",
        "settingsKey": "imageSettings",
        "labelsKey": "imageLabels",
        "default": [],
        "title": "Images",
        "description": "The images for this questionnaire.",
        "multilingual": false,
    }, {
        "key": "imageOverlays",
        "type": "list.resourceId",
        "isResourceIdList": "asset",
        "tabGroup": "questionnaireBackgrounds",
        "propertyType": "globalType",
        "inputType": "pageLayerSetting",
        "assetGroup": "images",
        "settingsKey": "imageOverlaySettings",
        "labelsKey": "imageOverlayLabels",
        "default": [],
        "title": "Image overlays",
        "description": "Image overlays to use for this questionnaire.",
        "multilingual": false,
        "hiddenFromInterface": true,
    }, {
        "key": "systemMaps",
        "type": "list.string",
        "tabGroup": "mapGroup",
        "propertyType": "globalType",
        "inputType": "pageSystemLayerSetting",
        "settingsKey": "systemMapSettings",
        "labelsKey": "systemMapLabels",
        "default": [],
        "title": "System maps",
        "description": "System maps provided by Maptionnaire. By using these you agree to the transfer of limited information (IP-address, location) to the relevant sub-contractor. See the privacy policies: <a href=\"https://www.mapbox.com/legal/privacy\" target=\"_blank\">Mapbox</a>, <a href=\"https://www.maptiler.com/privacy-policy/index.html\" target=\"_blank\">MapTiler</a>.",
        "multilingual": false,
    }, {
        "key": "baseMaps",
        "type": "list.resourceId",
        "isResourceIdList": "asset",
        "tabGroup": "mapGroup",
        "propertyType": "globalType",
        "inputType": "pageLayerSetting",
        "assetGroup": "maps",
        "settingsKey": "baseMapSettings",
        "labelsKey": "baseMapLabels",
        "default": [],
        "title": "Base maps",
        "description": "The base maps for this questionnaire.",
        "multilingual": false,
    }, {
        "key": "mapOverlays",
        "type": "list.resourceId",
        "isResourceIdList": "asset",
        "tabGroup": "mapGroup",
        "propertyType": "globalType",
        "inputType": "pageLayerSetting",
        "assetGroup": "maps",
        "settingsKey": "mapOverlaySettings",
        "labelsKey": "mapOverlayLabels",
        "default": [],
        "title": "Map overlays",
        "description": "The map overlays for this questionnaire.",
        "multilingual": false,
    }, {
        "key": "systemMapSettings",
        "type": "json",
        // Note: IDs of static system resources. Not really asset IDs.
        "jsonFormat": { "all": "list.resourceId", "separate": [{ "pageId": "string", "mapId": "resourceId" }] },
        "tabGroup": "autoFill",
        "propertyType": "globalType",
        "inputType": "autoFill",
        "default": null,
        "title": "System map settings",
        "description": "The system map settings.",
        "multilingual": false,
        "hiddenFromInterface": true,
        "getQuestionnaireElementGroupIds": IdFunctionAllSeparateListsGet(false, "pageId"),
        "replaceQuestionnaireElementGroupIds": IdFunctionAllSeparateListsReplace(false, "pageId"),
    }, {
        "key": "baseMapSettings",
        "type": "json",
        "jsonFormat": { "all": "list.resourceId", "separate": [{ "pageId": "string", "mapId": "resourceId" }] },
        "tabGroup": "autoFill",
        "propertyType": "globalType",
        "inputType": "autoFill",
        "default": null,
        "title": "Base map settings",
        "description": "The base map settings.",
        "multilingual": false,
        "hiddenFromInterface": true,
        "getAssetIds": IdFunctionAllSeparateListsGet(true, "mapId"),
        "replaceAssetIds": IdFunctionAllSeparateListsReplace(true, "mapId"),
        "getQuestionnaireElementGroupIds": IdFunctionAllSeparateListsGet(false, "pageId"),
        "replaceQuestionnaireElementGroupIds": IdFunctionAllSeparateListsReplace(false, "pageId"),
    }, {
        "key": "mapOverlaySettings",
        "type": "json",
        "jsonFormat": { "all": "list.resourceId", "separate": [{ "pageId": "string", "mapId": "resourceId" }] },
        "tabGroup": "autoFill",
        "propertyType": "globalType",
        "inputType": "autoFill",
        "default": null,
        "title": "Map overlay settings",
        "description": "The map overlay settings.",
        "multilingual": false,
        "hiddenFromInterface": true,
        "getAssetIds": IdFunctionAllSeparateListsGet(true, "mapId"),
        "replaceAssetIds": IdFunctionAllSeparateListsReplace(true, "mapId"),
        "getQuestionnaireElementGroupIds": IdFunctionAllSeparateListsGet(false, "pageId"),
        "replaceQuestionnaireElementGroupIds": IdFunctionAllSeparateListsReplace(false, "pageId"),
    }, {
        "key": "imageSettings",
        "type": "json",
        "jsonFormat": { "all": "list.resourceId", "separate": [{ "pageId": "string", "mapId": "resourceId" }] },
        "tabGroup": "autoFill",
        "propertyType": "globalType",
        "inputType": "autoFill",
        "default": null,
        "title": "Image settings",
        "description": "The image settings.",
        "multilingual": false,
        "hiddenFromInterface": true,
        "getAssetIds": IdFunctionAllSeparateListsGet(true, "mapId"),
        "replaceAssetIds": IdFunctionAllSeparateListsReplace(true, "mapId"),
        "getQuestionnaireElementGroupIds": IdFunctionAllSeparateListsGet(false, "pageId"),
        "replaceQuestionnaireElementGroupIds": IdFunctionAllSeparateListsReplace(false, "pageId"),
    }, {
        "key": "imageOverlaySettings",
        "type": "json",
        "jsonFormat": { "all": "list.resourceId", "separate": [{ "pageId": "string", "mapId": "resourceId" }] },
        "tabGroup": "autoFill",
        "propertyType": "globalType",
        "inputType": "autoFill",
        "default": null,
        "title": "Image overlay settings",
        "description": "The image overlay settings.",
        "multilingual": false,
        "hiddenFromInterface": true,
        "getAssetIds": IdFunctionAllSeparateListsGet(true, "mapId"),
        "replaceAssetIds": IdFunctionAllSeparateListsReplace(true, "mapId"),
        "getQuestionnaireElementGroupIds": IdFunctionAllSeparateListsGet(false, "pageId"),
        "replaceQuestionnaireElementGroupIds": IdFunctionAllSeparateListsReplace(false, "pageId"),
    }, {
        "key": "systemMapLabels",
        "type": "json",
        // Note: IDs of static system resources. Not really asset IDs.
        "jsonFormat": { "[assetId]": "string" },
        "tabGroup": "autoFill",
        "propertyType": "globalType",
        "inputType": "autoFill",
        "default": null,
        "title": "System map labels",
        "description": "The system map labels used in the layer switcher.",
        "multilingual": false,
        "hiddenFromInterface": true,
    }, {
        "key": "baseMapLabels",
        "type": "json",
        "jsonFormat": { "[assetId]": "string" },
        "tabGroup": "autoFill",
        "propertyType": "globalType",
        "inputType": "autoFill",
        "default": null,
        "title": "Base map labels",
        "description": "The base map labels used in the layer switcher.",
        "multilingual": false,
        "hiddenFromInterface": true,
        "getAssetIds": IdFunctionMapGet,
        "replaceAssetIds": IdFunctionMapReplace,
    }, {
        "key": "mapOverlayLabels",
        "type": "json",
        "jsonFormat": { "[assetId]": "string" },
        "tabGroup": "autoFill",
        "propertyType": "globalType",
        "inputType": "string",
        "default": null,
        "title": "Map overlay labels",
        "description": "The map overlay labels used in the layer switcher.",
        "multilingual": false,
        "hiddenFromInterface": true,
        "getAssetIds": IdFunctionMapGet,
        "replaceAssetIds": IdFunctionMapReplace,
    }, {
        "key": "imageLabels",
        "type": "json",
        "jsonFormat": { "[assetId]": "string" },
        "tabGroup": "autoFill",
        "propertyType": "globalType",
        "inputType": "string",
        "default": null,
        "title": "Image labels",
        "description": "The image labels used in the layer switcher.",
        "multilingual": false,
        "hiddenFromInterface": true,
        "getAssetIds": IdFunctionMapGet,
        "replaceAssetIds": IdFunctionMapReplace,
    }, {
        "key": "imageOverlayLabels",
        "type": "json",
        "jsonFormat": { "[assetId]": "string" },
        "tabGroup": "autoFill",
        "propertyType": "globalType",
        "inputType": "string",
        "default": null,
        "title": "Image overlay labels",
        "description": "The image overlay labels used in the layer switcher.",
        "multilingual": false,
        "hiddenFromInterface": true,
        "getAssetIds": IdFunctionMapGet,
        "replaceAssetIds": IdFunctionMapReplace,
    },
    // DEPRECIATED
    // DEPRECIATED
    //  {
    //     "key": "tridifyModels",
    //     "type": "list.resourceId",
    //     "isResourceIdList": "asset",
    //     "tabGroup": "questionnaire3d",
    //     "propertyType": "globalType",
    //     "inputType": "pageLayerSetting",
    //     "assetGroup": "maps",
    //     "settingsKey": "tridifyModelSettings",
    //     "default": [],
    //     "title": "Tridify conversion",
    //     "description": "The Tridify model to use for this questionnaire.",
    //     "multilingual": false,
    //     "needsLicense": "feature:questionnaire:3d",
    //     "hiddenFromInterface": true,
    //  }, {
    //     "key": "tridifyModelSettings",
    //     "type": "json",
    //     "jsonFormat": {"all": "list.resourceId", "separate": [{"pageId": "string", "mapId": "resourceId"}]},
    //     "tabGroup": "autoFill",
    //     "propertyType": "globalType",
    //     "inputType": "autoFill",
    //     "default": null,
    //     "title": "Tridify model settings",
    //     "description": "The Tridify model settings.",
    //     "multilingual": false,
    //     "needsLicense": "feature:questionnaire:3d",
    //     "hiddenFromInterface": true,
    //     "getAssetIds": IdFunctionAllSeparateListsGet(true, "mapId"),
    //     "replaceAssetIds": IdFunctionAllSeparateListsReplace(true, "mapId"),
    //     "getQuestionnaireElementGroupIds": IdFunctionAllSeparateListsGet(false, "pageId"),
    //     "replaceQuestionnaireElementGroupIds": IdFunctionAllSeparateListsReplace(false, "pageId"),
    //  }, 
    // DEPRECIATED
    // DEPRECIATED
    {
        "key": "sova3dAppUrl",
        "type": "url",
        "tabGroup": "questionnaire3d",
        "propertyType": "globalType",
        "inputType": "url",
        "default": null,
        "title": "Sova3D app url",
        "description": "The url to the Sova3D app",
        "multilingual": false,
        "needsLicense": "feature:questionnaire:3d",
    },
    // //////////////////////// PAGE SETTINGS ////////////////////////////////////////////
    //
    // Per-page settings that govern the behavior of the individual pages
    //
    // ///////////////////////////////////////////////////////////////////////////////////
    {
        "key": "pageTitles",
        "type": "json",
        "jsonFormat": { "[pageId]": "string" },
        "tabGroup": "questionnaireGeneral",
        "propertyType": "pageType",
        "inputType": "text",
        "default": null,
        "title": "Page title",
        "description": "A title that can be show to the respondent.",
        "multilingual": true,
        "getQuestionnaireElementGroupIds": IdFunctionMapGet,
        "replaceQuestionnaireElementGroupIds": IdFunctionMapReplace,
    }, {
        "key": "pageTypes",
        "type": "json",
        "jsonFormat": { "[pageId]": "string" },
        "tabGroup": "questionnaireGeneral",
        "propertyType": "pageType",
        "inputType": "ownOptionList",
        "valueOptions": PAGE_TYPE_OPTIONS,
        "valueOptionTexts": PAGE_TYPE_OPTION_TEXTS,
        "valueOptionNeedsLicense": PAGE_TYPE_OPTION_NEEDS_LICENSE,
        "default": null,
        "title": "Page type",
        "description": "What kind of background to use for this page.",
        "multilingual": false,
        "getQuestionnaireElementGroupIds": IdFunctionMapGet,
        "replaceQuestionnaireElementGroupIds": IdFunctionMapReplace,
    }, {
        "key": "branchingLogic",
        "type": "json",
        "jsonFormat": { "[foo]": "bar" }, // not used anyway
        "tabGroup": "questionnaireGeneral",
        "propertyType": "globalType",
        "inputType": "custom",
        "hiddenFromInterface": true,
        "default": null,
        "multilingual": false,
    }, {
        "key": "legendBuilder",
        "type": "json",
        "jsonFormat": { "[foo]": "bar" }, // not used anyway
        "tabGroup": "questionnaireGeneral",
        "propertyType": "globalType",
        "inputType": "custom",
        "hiddenFromInterface": true,
        "default": null,
        "multilingual": true,
    }, {
        "key": "pageLayouts",
        "type": "json",
        "jsonFormat": { "[pageId]": "string" },
        "tabGroup": "questionnaireStyle",
        "propertyType": "pageType",
        "inputType": "ownOptionList",
        "valueOptions": PAGE_LAYOUT_OPTIONS,
        "valueOptionTexts": PAGE_LAYOUT_OPTION_TEXTS,
        "default": null,
        "title": "Page layout",
        "description": "What kind of layout to use for this page.",
        "multilingual": false,
        "getQuestionnaireElementGroupIds": IdFunctionMapGet,
        "replaceQuestionnaireElementGroupIds": IdFunctionMapReplace,
    }, {
        "key": "pageAlignments",
        "type": "json",
        "jsonFormat": { "[pageId]": "string" },
        "tabGroup": "questionnaireStyle",
        "propertyType": "pageType",
        "inputType": "ownOptionList",
        "valueOptions": PAGE_ALIGNMENT_OPTIONS,
        "valueOptionTexts": PAGE_ALIGNMENT_OPTION_TEXTS,
        "default": null,
        "title": "Page alignment",
        "description": "What kind of alignment to use for this page.",
        "multilingual": false,
        "getQuestionnaireElementGroupIds": IdFunctionMapGet,
        "replaceQuestionnaireElementGroupIds": IdFunctionMapReplace,
    }, {
        "key": "pageBackgroundColors",
        "type": "json",
        "jsonFormat": { "[pageId]": "string" },
        "tabGroup": "questionnaireStyle",
        "propertyType": "pageType",
        "inputType": "color",
        "default": null,
        "title": "Background color",
        "description": "The background color to use for this page.",
        "multilingual": false,
        "getQuestionnaireElementGroupIds": IdFunctionMapGet,
        "replaceQuestionnaireElementGroupIds": IdFunctionMapReplace,
    }, {
        "key": "pageOriginZooms",
        "type": "json",
        "jsonFormat": { "[pageId]": "number" },
        "tabGroup": "questionnaireBackgrounds",
        "propertyType": "pageType",
        "inputType": "custom",
        "default": null,
        "title": "Page initial zoom level",
        "description": "Show the map at this zoom level for this page.",
        "multilingual": false,
        "hiddenFromInterface": true,
        "getQuestionnaireElementGroupIds": IdFunctionMapGet,
        "replaceQuestionnaireElementGroupIds": IdFunctionMapReplace,
    }, {
        "key": "pageOriginLonLats",
        "type": "json",
        "jsonFormat": { "[pageId]": "list.number" },
        "tabGroup": "questionnaireBackgrounds",
        "propertyType": "pageType",
        "inputType": "custom",
        "default": null,
        "title": "Initial map location",
        "description": "Show this location on the map for this page.",
        "multilingual": false,
        "hiddenFromInterface": true,
        "getQuestionnaireElementGroupIds": IdFunctionMapGet,
        "replaceQuestionnaireElementGroupIds": IdFunctionMapReplace,
    }, {
        "key": "pageCombinePopups",
        "type": "json",
        "jsonFormat": { "[pageId]": "boolean" },
        "tabGroup": "questionnaireBackgrounds",
        "propertyType": "pageType",
        "inputType": "switch",
        "default": null,
        "title": "Combine popups for overlapping layers",
        "description": "Switch this on to combine overlapping",
        "multilingual": false,
        "getQuestionnaireElementGroupIds": IdFunctionMapGet,
        "replaceQuestionnaireElementGroupIds": IdFunctionMapReplace,
    }, {
        // This is technically a resource ID, but the global setting is the source of truth 
        // about available maps, and this setting will gracefully fail if unavailable ID's
        // are listed
        "key": "pageSelectedBackgroundImage",
        "type": "json",
        "jsonFormat": { "[pageId]": "string" },
        "tabGroup": "questionnaireStyle",
        "propertyType": "pageType",
        "inputType": "custom",
        "assetGroup": "images",
        "default": null,
        "title": "Selected background image",
        "description": "The background image for this page.",
        "multilingual": false,
        "getQuestionnaireElementGroupIds": IdFunctionMapGet,
        "replaceQuestionnaireElementGroupIds": IdFunctionMapReplace,
    }, {
        // This is technically a resource ID, but the global setting is the source of truth 
        // about available maps, and this setting will gracefully fail if unavailable ID's
        // are listed
        "key": "pageSelectedBaseMap",
        "type": "json",
        "jsonFormat": { "[pageId]": "string" },
        "tabGroup": "questionnaireBackgrounds",
        "propertyType": "pageType",
        "inputType": "custom",
        "assetGroup": "maps",
        "default": null,
        "title": "Selected base map",
        "description": "The map switches to the selected base map for this page.",
        "multilingual": false,
        "getQuestionnaireElementGroupIds": IdFunctionMapGet,
        "replaceQuestionnaireElementGroupIds": IdFunctionMapReplace,
    }, {
        // usage of pageSelectedImage is a little unclear. it's added when setting "hide by default"
        // for an interactive image on a page and probably only kept alive for the respondent view
        "key": "pageSelectedImage",
        "type": "json",
        "jsonFormat": { "[pageId]": { "[assetId]": "boolean" } },
        "tabGroup": "questionnaireBackgrounds",
        "propertyType": "pageType",
        "inputType": "custom",
        "assetGroup": "maps",
        "default": null,
        "title": "Selected image",
        "description": "The questionnaire switches to the selected image for this page.",
        "multilingual": false,
        "getQuestionnaireElementGroupIds": IdFunctionMapGet,
        "replaceQuestionnaireElementGroupIds": IdFunctionMapReplace,
    }, {
        // This is technically a resource ID, but the global setting is the source of truth 
        // about available maps, and this setting will gracefully fail if unavailable ID's
        // are listed
        "key": "pageSelectedMapOverlays",
        "type": "json",
        "jsonFormat": { "[pageId]": { "[assetId]": "boolean" } },
        "tabGroup": "questionnaireBackgrounds",
        "propertyType": "pageType",
        "inputType": "custom",
        "assetGroup": "maps",
        "default": null,
        "title": "Page's map overlays",
        "description": "The map overlays that are visible on this page.",
        "multilingual": false,
        "getQuestionnaireElementGroupIds": IdFunctionMapGet,
        "replaceQuestionnaireElementGroupIds": IdFunctionMapReplace,
        "getAssetIds": IdFunctionNestedMapGet,
        "replaceAssetIds": IdFunctionNestedMapReplace,
    }, {
        // This is technically a resource ID, but the global setting is the source of truth 
        // about available maps, and this setting will gracefully fail if unavailable ID's
        // are listed
        "key": "pageSelectedImageOverlays",
        "type": "json",
        "jsonFormat": { "[pageId]": { "[assetId]": "boolean" } },
        "tabGroup": "questionnaireBackgrounds",
        "propertyType": "pageType",
        "inputType": "custom",
        "assetGroup": "images",
        "default": null,
        "title": "Page's image overlays",
        "description": "The image overlays that are visible on this page.",
        "multilingual": false,
        "hiddenFromInterface": true,
        "getQuestionnaireElementGroupIds": IdFunctionMapGet,
        "replaceQuestionnaireElementGroupIds": IdFunctionMapReplace,
        "getAssetIds": IdFunctionNestedMapGet,
        "replaceAssetIds": IdFunctionNestedMapReplace,
    },
    // DEPRECIATED
    // DEPRECIATED
    //  {
    //     // This is technically a resource ID, but the global setting is the source of truth 
    //     // about available maps, and this setting will gracefully fail if unavailable ID's
    //     // are listed
    //     "key": "pageSelectedTridifyModel",
    //     "type": "json",
    //     "jsonFormat": {"[pageId]": "string"},
    //     "tabGroup": "questionnaire3d",
    //     "propertyType": "pageType",
    //     "inputType": "custom",
    //     "assetGroup": "models",
    //     "default": null,
    //     "title": "Selected Tridify model",
    //     "description": "The questionnaire switches to the selected Tridify model for this page.",
    //     "multilingual": false,
    //     "getQuestionnaireElementGroupIds": IdFunctionMapGet,
    //     "replaceQuestionnaireElementGroupIds": IdFunctionMapReplace,
    //  }, {
    //     "key": "pageTridifyCameraData",
    //     "type": "json",
    //     "jsonFormat": {"[pageId]": "json"},
    //     "tabGroup": "questionnaire3d",
    //     "propertyType": "pageType",
    //     "inputType": "custom",
    //     "default": null,
    //     "title": "Page camera data",
    //     "description": "Switch the Tridify model's camera position for this page.",
    //     "multilingual": false,
    //     "hiddenFromInterface": true,
    //     "getQuestionnaireElementGroupIds": IdFunctionMapGet,
    //     "replaceQuestionnaireElementGroupIds": IdFunctionMapReplace,
    //  }, 
    // DEPRECIATED
    // DEPRECIATED
    {
        "key": "pageSova3dProject",
        "type": "json",
        "jsonFormat": { "[pageId]": "string" },
        "tabGroup": "questionnaire3d",
        "propertyType": "pageType",
        "inputType": "custom",
        "assetGroup": "models",
        "default": null,
        "title": "Selected Sova3D Project",
        "description": "The questionnaire switches to the selected Sova3D project for this page.",
        "multilingual": false,
        "getQuestionnaireElementGroupIds": IdFunctionMapGet,
        "replaceQuestionnaireElementGroupIds": IdFunctionMapReplace,
    }, {
        "key": "pageSova3dCameraData",
        "type": "json",
        "jsonFormat": { "[pageId]": "json" },
        "tabGroup": "questionnaire3d",
        "propertyType": "pageType",
        "inputType": "custom",
        "default": null,
        "title": "Page camera data",
        "description": "Switch the map's camera position for this page.",
        "multilingual": false,
        "hiddenFromInterface": true,
        "getQuestionnaireElementGroupIds": IdFunctionMapGet,
        "replaceQuestionnaireElementGroupIds": IdFunctionMapReplace,
    },
    // DEPCRECIATED
    {
        // This is technically a resource ID, but the global setting is the source of truth 
        // about available maps, and this setting will gracefully fail if unavailable ID's
        // are listed
        "key": "pageAssetsHiddenFromLayerControl",
        "type": "json",
        "jsonFormat": { "[pageId]": { "[assetId]": "boolean" } },
        "tabGroup": "questionnaireBackgrounds",
        "propertyType": "pageType",
        "inputType": "custom",
        "assetGroup": "maps",
        "default": null,
        "title": "Hide the map from the layer switcher",
        "description": "Hide the map from the layer switcher so it cannot be toggled.",
        "multilingual": false,
        "getQuestionnaireElementGroupIds": IdFunctionMapGet,
        "replaceQuestionnaireElementGroupIds": IdFunctionMapReplace,
        "getAssetIds": IdFunctionNestedMapGet,
        "replaceAssetIds": IdFunctionNestedMapReplace,
        "hiddenFromInterface": true,
        "isDeprecated": true,
    },
    // DEPRECATED
    // //////////////////////// REPORT SETTINGS //////////////////////////////////////////
    //
    // Settings that govern PDF report generation
    //
    // ///////////////////////////////////////////////////////////////////////////////////
    // DEPRECATED
    {
        "key": "isReportingEnabled",
        "type": "boolean",
        "tabGroup": "questionnaireReport",
        "propertyType": "globalType",
        "inputType": "switch",
        "default": null,
        "title": "Enable reporting",
        "description": "Use this to enable reporting for this questionnaire.",
        "hiddenFromInterface": true,
        "multilingual": false,
        "isDeprecated": true,
    }, {
        "key": "reportHeaderLeft",
        "type": "list.string",
        "tabGroup": "questionnaireReport",
        "propertyType": "globalType",
        "inputType": "textItemMany",
        "maxTags": 3,
        "default": null,
        "title": "Left header",
        "description": "The left header to use on a report page.",
        "multilingual": true,
        "isDeprecated": true,
    }, {
        "key": "reportHeaderCenter",
        "type": "list.string",
        "tabGroup": "questionnaireReport",
        "propertyType": "globalType",
        "inputType": "textItem",
        "default": null,
        "title": "Center header",
        "description": "The center header to use on a report page.",
        "multilingual": true,
        "isDeprecated": true,
    }, {
        "key": "reportHeaderRight",
        "type": "list.string",
        "tabGroup": "questionnaireReport",
        "propertyType": "globalType",
        "inputType": "textItemMany",
        "maxTags": 3,
        "default": null,
        "title": "Right header",
        "description": "The right header to use on a report page.",
        "multilingual": true,
        "isDeprecated": true,
    }, {
        "key": "reportFooterLeft",
        "type": "list.string",
        "tabGroup": "questionnaireReport",
        "propertyType": "globalType",
        "inputType": "textItemMany",
        "maxTags": 3,
        "default": null,
        "title": "Left footer",
        "description": "The left footer to use on a report page.",
        "multilingual": true,
        "isDeprecated": true,
    }, {
        "key": "reportFooterCenter",
        "type": "list.string",
        "tabGroup": "questionnaireReport",
        "propertyType": "globalType",
        "inputType": "textItem",
        "default": null,
        "title": "Center footer",
        "description": "The center footer to use on a report page.",
        "multilingual": true,
        "isDeprecated": true,
    }, {
        "key": "reportFooterRight",
        "type": "list.string",
        "tabGroup": "questionnaireReport",
        "propertyType": "globalType",
        "inputType": "textItemMany",
        "maxTags": 3,
        "default": null,
        "title": "Right footer",
        "description": "The right footer to use on a report page.",
        "multilingual": true,
        "isDeprecated": true,
    }, {
        "key": "reportTitle",
        "type": "string",
        "tabGroup": "questionnaireReport",
        "propertyType": "globalType",
        "inputType": "text",
        "default": null,
        "title": "Report title",
        "description": "The title that will be show on the summary page of the report.",
        "multilingual": true,
        "isDeprecated": true,
    }, {
        "key": "reportType",
        "type": "string",
        "tabGroup": "questionnaireReport",
        "propertyType": "globalType",
        "inputType": "text",
        "default": null,
        "title": "Report type",
        "description": "The type that will be show on the summary page of the report.",
        "multilingual": true,
        "isDeprecated": true,
    }, {
        "key": "reportDescription",
        "type": "string",
        "tabGroup": "questionnaireReport",
        "propertyType": "globalType",
        "inputType": "textarea",
        "default": null,
        "title": "Report description",
        "description": "The description that will be show on the summary page of the report.",
        "multilingual": true,
        "isDeprecated": true,
    },
    // DEPRECATED
    {
        "key": "sendRespondentEmail",
        "type": "boolean",
        "inputType": "switch",
        "tabGroup": "questionnaireReportEmail",
        "propertyType": "globalType",
        "default": null,
        "title": "Send report to respondent",
        "description": "Select this if you want to send the report to the respondent.",
        "multilingual": false,
    }, {
        "key": "reportDefaultMap",
        "type": "resourceId",
        "isResourceIdList": "asset",
        "assetGroup": "maps",
        "assetType": "wmtsmap",
        "tabGroup": "questionnaireReportEmail",
        "propertyType": "globalType",
        "inputType": "mapAssetId",
        "default": null,
        "title": "Report map",
        "description": "The map to use as the background for geo responses in the PDF report. The map must be a WSM map.",
        "multilingual": false,
    }, {
        "key": "respondentEmailSubject",
        "type": "string",
        "inputType": "text",
        "tabGroup": "questionnaireReportEmail",
        "propertyType": "globalType",
        "default": null,
        "native.maxlength": 255,
        "title": "Email subject",
        "description": "The subject line for the email sent to the respondent.",
        "multilingual": true,
    }, {
        "key": "respondentEmailMessage",
        "type": "string",
        "inputType": "textarea",
        "tabGroup": "questionnaireReportEmail",
        "propertyType": "globalType",
        "default": null,
        "title": "Email message",
        "description": "The message for the email sent to the respondent.",
        "multilingual": true,
    }, {
        "key": "emailAddress",
        "type": "list.string",
        "inputType": "text",
        "tabGroup": "basic",
        "group": "reportEmailGroup",
        "default": [],
        "defaultInsert": "",
        "title": "Email address",
        "description": "The email address for this recipient.",
        "multilingual": false,
    }, {
        "key": "emailSubject",
        "type": "list.string",
        "inputType": "text",
        "tabGroup": "basic",
        "group": "reportEmailGroup",
        "default": [],
        "defaultInsert": "",
        "native.maxlength": 255,
        "title": "Email subject",
        "description": "The subject line for the email sent to this recipient.",
        "multilingual": false,
    }, {
        "key": "emailMessage",
        "type": "list.string",
        "inputType": "textarea",
        "tabGroup": "basic",
        "group": "reportEmailGroup",
        "default": [],
        "defaultInsert": "",
        "title": "Email message",
        "description": "The message for the email sent to this recipient.",
        "multilingual": false,
    },
    // Still used in conjunction with the other email settings, but hidden from UI so it cannot be turned off
    {
        "key": "emailIncludeReport",
        "type": "list.boolean",
        "inputType": "switch",
        "tabGroup": "extra",
        "group": "reportEmailGroup",
        "default": [],
        "defaultInsert": true,
        "title": "Include report",
        "description": "Select this to have the report attached to the email message.",
        "multilingual": false,
        "hiddenFromInterface": true,
    },
    {
        "key": "emailIncludeAttachements",
        "type": "list.boolean",
        "inputType": "switch",
        "tabGroup": "extra",
        "group": "reportEmailGroup",
        "default": [],
        "defaultInsert": false,
        "title": "Include attachments",
        "description": "Select this to have the respondents uploads attached to the email message.",
        "multilingual": false,
        "hiddenFromInterface": true,
    }, {
        "key": "emailUseLinks",
        "type": "list.boolean",
        "inputType": "switch",
        "tabGroup": "extra",
        "group": "reportEmailGroup",
        "default": [],
        "defaultInsert": false,
        "title": "Use download links",
        "description": "Select this to have the attachments included as download links.",
        "multilingual": false,
        "hiddenFromInterface": true,
    }, {
        "key": "emailMaxMessageSize",
        "type": "list.number",
        "inputType": "integer",
        "tabGroup": "extra",
        "group": "reportEmailGroup",
        "default": [],
        "min": 0,
        "max": 50,
        "defaultInsert": 10,
        "title": "Maximum size (in MB)",
        "description": "The maximum allowed size for the email message, if the combined size of the PDF report and attachments exceed this limit, download links to the attachments will be inserted instead.",
        "multilingual": false,
        "hiddenFromInterface": true,
    },
    // //////////////////////// ANALYSIS TOOL SETTINGS ////////////////////////////////////////////
    //
    // DEPRECIATED 2024. See PR #2163
    // Settings that govern questionnaire's analysis tool
    //
    // ///////////////////////////////////////////////////////////////////////////////////
    // DEPRECIATED
    // DEPRECIATED
    {
        "key": "visitorChartColorPalette",
        "type": "list.string",
        "group": "hidden",
        "tabGroup": "",
        "inputType": "color",
        "default": [],
        "defaultInsert": "",
        "title": "Chart color",
        "description": "The color to use for this option in the chart. Use the color selector or specify a hex value.",
        "multilingual": false,
        "hiddenFromInterface": true,
        "isDeprecated": true,
    }, {
        "key": "visitorTimelineColorPalette",
        "type": "list.string",
        "group": "hidden",
        "tabGroup": "",
        "inputType": "color",
        "default": [],
        "defaultInsert": "",
        "title": "Chart color",
        "description": "The color to use for this option in the chart. Use the color selector or specify a hex value.",
        "multilingual": false,
        "hiddenFromInterface": true,
        "isDeprecated": true,
    }
    // DEPRECIATED
    // DEPRECIATED
];
for (const property of QuestionnairePropertyList) {
    if (QuestionnairePropertyMap[property.key]) {
        throw new Error(`Duplicate questionnaire property key name "${property.key}".`);
    }
    QuestionnairePropertyMap[property.key] = property;
}
//# sourceMappingURL=questionnaireProperties.js.map